import { useState, useEffect, useCallback } from 'react'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { botsApi, type Bot } from '../../api/bots'
import { mediaApi } from '../../api/media'
import { chatsApi } from '../../api/chats'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'

function StarRating({ botId: _botId, current, onRate }: { botId: string; current: number; onRate: (score: number) => void }) {
  const [hover, setHover] = useState(0)
  return (
    <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onRate(s)}
          className="text-base transition-transform hover:scale-110"
          title={`Rate ${s} stars`}
        >
          <span className={(hover || Math.round(current)) >= s ? 'text-yellow-400' : 'text-txt-meta/40'}>★</span>
        </button>
      ))}
    </div>
  )
}

const CATEGORIES = ['all', 'utility', 'games', 'news', 'shopping', 'productivity', 'entertainment']

interface Props {
  onClose: () => void
}

export function BotStore({ onClose }: Props) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('all')
  const [bots, setBots] = useState<Bot[]>([])
  const [loading, setLoading] = useState(false)
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [userRatings, setUserRatings] = useState<Record<string, number>>({})
  const { upsertChat, setActiveChat } = useChatStore()
  const { addToast } = useUiStore()

  const load = useCallback(async (q: string, cat: string) => {
    setLoading(true)
    try {
      let result: Bot[]
      if (q.trim().length >= 2) {
        const r = await botsApi.search(q.trim())
        result = r.bots ?? []
      } else {
        const r = await botsApi.list(cat === 'all' ? undefined : cat)
        result = r.bots ?? []
      }
      setBots(result)
    } catch {
      setBots([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => load(query, category), 300)
    return () => clearTimeout(t)
  }, [query, category, load])

  const handleRate = async (botId: string, score: number) => {
    try {
      await botsApi.rate(botId, score)
      setUserRatings((prev) => ({ ...prev, [botId]: score }))
      addToast('success', `Rated ${score} ★`)
    } catch {
      addToast('error', 'Не удалось поставить оценку')
    }
  }

  const openBot = async (bot: Bot) => {
    setOpeningId(bot.id)
    try {
      const chat = await chatsApi.getOrCreate(bot.id)
      upsertChat({
        ...chat,
        peer: {
          id: bot.id,
          username: bot.username,
          first_name: bot.name,
          last_name: '',
          bio: bot.about,
          phone: '',
          avatar_url: bot.avatar_file_id ? mediaApi.getURL(bot.avatar_file_id) : '',
          is_verified: false,
          is_premium: false,
          is_bot: true,
          online: true,
          last_seen_at: null,
        },
      })
      setActiveChat(chat.id)
      onClose()
    } catch {
      addToast('error', 'Не удалось открыть чат с ботом')
    } finally {
      setOpeningId(null)
    }
  }

  return (
    <Modal onClose={onClose} title="Магазин ботов">
      <div className="p-3 flex flex-col gap-3">
        {/* Search */}
        <input
          autoFocus
          type="text"
          placeholder="Поиск ботов..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
        />

        {/* Category chips */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-thin">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors
                ${category === cat ? 'bg-accent text-white' : 'bg-bg-input text-txt-secondary hover:bg-bg-hover'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Bot list */}
        <div className="space-y-0.5 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : bots.length === 0 ? (
            <p className="text-txt-meta text-sm text-center py-8">No bots found</p>
          ) : (
            bots.map((bot) => (
              <button
                key={bot.id}
                onClick={() => openBot(bot)}
                disabled={openingId === bot.id}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-hover transition-colors text-left disabled:opacity-60"
              >
                <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center shrink-0 overflow-hidden">
                  {bot.avatar_file_id ? (
                    <img
                      src={mediaApi.getThumbnailURL(bot.avatar_file_id, '200x200')}
                      alt={bot.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-accent font-bold text-sm">{bot.name[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-txt-primary text-sm font-medium truncate">{bot.name}</p>
                    <span className="shrink-0 text-accent text-[10px] bg-accent/15 px-1.5 py-0.5 rounded-full">BOT</span>
                  </div>
                  <p className="text-txt-secondary text-xs truncate">@{bot.username}</p>
                  {bot.description && (
                    <p className="text-txt-meta text-xs truncate">{bot.description}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {bot.rating > 0 && (
                    <span className="text-txt-meta text-xs">⭐ {bot.rating.toFixed(1)}</span>
                  )}
                  <StarRating
                    botId={bot.id}
                    current={userRatings[bot.id] ?? 0}
                    onRate={(score) => handleRate(bot.id, score)}
                  />
                </div>
                {openingId === bot.id && <Spinner size={16} />}
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  )
}
